/**
 * A operação muda de obra quando a versão é aprovada (Fase 36 — RN-465 a RN-469).
 *
 * A versão de orçamento é uma cópia da obra aprovada, com ids novos no projeto e no orçamento
 * (pavimentos, frentes, tipologias, cômodos, serviços da obra…). A operação — funcionários,
 * cronograma, planejamento, RDO, peças e cômodos executados, qualidade, suprimento — mora na
 * aprovada e aponta para as linhas **dela**. Aprovar a versão leva essa operação para a versão
 * (decisão do Gilvan, 20/10/2026), e cada referência ao projeto precisa achar o seu par na
 * cópia: é o que este módulo calcula, sem banco.
 *
 * - **O par** (RN-464, RN-466): pela origem da cópia — as duas linhas vêm da mesma raiz —, e,
 *   sem ela, pelo nome entre as linhas da versão que não têm par pela origem (a versão criada
 *   antes da `0083`, ou a linha refeita nela com o mesmo nome).
 * - **Sem lugar** (RN-467): a referência obrigatória sem par para a aprovação. Escolhe-se outro
 *   alvo do mesmo tipo na versão, ou deixar na antiga as linhas que apontam para ele — salvo as
 *   que não podem ficar (a alocação é do recurso, que vai; `0008`).
 * - **No mesmo lugar** (RN-468): duas linhas com a mesma chave única na obra nova. Escolhe-se a
 *   que vale; a outra fica na antiga, ou — sendo da versão — sai.
 * - A linha de operação que depende de outra (a revisão do item, a mão de obra do item, o anexo
 *   do RDO) vai e fica junto com ela.
 */

/** O tipo da linha do projeto ou do orçamento que a operação aponta. */
export type TransferTargetKind =
  | 'ACTIVITY'
  | 'FLOOR'
  | 'UNIT_POSITION'
  | 'ROOM'
  | 'UNIT_TYPE'
  | 'SERVICE'
  | 'DISCIPLINE'
  | 'CHECKLIST'
  | 'CALENDAR'
  | 'DRAWN_PIECE'
  | 'PIECE';

export const TRANSFER_TARGET_LABEL: Readonly<Record<TransferTargetKind, string>> = {
  ACTIVITY: 'Frente',
  FLOOR: 'Pavimento',
  UNIT_POSITION: 'Posição',
  ROOM: 'Cômodo',
  UNIT_TYPE: 'Tipologia',
  SERVICE: 'Serviço',
  DISCIPLINE: 'Disciplina',
  CHECKLIST: 'Ficha de verificação',
  CALENDAR: 'Calendário',
  DRAWN_PIECE: 'Peça desenhada',
  PIECE: 'Peça',
};

/** O nome de cada tabela da operação, como a tela o mostra. */
export const TRANSFER_TABLE_LABEL: Readonly<Record<string, string>> = {
  employees: 'Funcionários',
  external_services: 'Terceirizados',
  execution_resources: 'Recursos da execução',
  teams: 'Equipes',
  team_members: 'Membros das equipes',
  team_disciplines: 'Disciplinas das equipes',
  schedules: 'Cronograma importado',
  schedule_imports: 'Importações do cronograma',
  schedule_entries: 'Linhas de compra',
  schedule_entry_revisions: 'Histórico das linhas de compra',
  supply_acknowledgements: 'Ciências do suprimento',
  material_requisitions: 'Requisições de material',
  schedule_items: 'Itens do cronograma',
  schedule_item_labor: 'Mão de obra dos itens',
  weekly_plans: 'Planos semanais',
  weekly_allocations: 'Alocações',
  planning_changes: 'Histórico do planejamento',
  daily_inspections: 'Conferências do dia',
  daily_reports: 'Relatórios diários (RDO)',
  service_verifications: 'Verificações de qualidade',
  non_conformities: 'Não conformidades',
  team_measurements: 'Medições por equipe',
  piece_executions: 'Peças executadas',
  room_progress: 'Avanço por cômodo',
  attachments: 'Fotos e anexos',
};

/**
 * Uma linha do projeto ou do orçamento de uma das duas obras — o alvo de uma referência.
 *
 * A peça da planilha não tem linha própria: o alvo `PIECE` é `<tipologia>|<NOME>`, e o par dele
 * é o mesmo nome na tipologia que é o par da tipologia. O `naturalKey` da peça é o nome com que a
 * planilha a escreve (`piece_name_aliases`, RN-458): a peça que uma das obras renomeou acha a
 * da outra por ele.
 */
export interface TransferTarget {
  readonly kind: TransferTargetKind;
  readonly id: string;
  /** Como a pessoa o reconhece: "Torre A › 3º Pavimento › Alvenaria". */
  readonly label: string;
  /** O nome comparável, para o par sem origem (RN-466). Vazio: só pela origem. */
  readonly naturalKey: string;
}

export interface OperationRowRef {
  readonly kind: TransferTargetKind;
  readonly id: string;
  /** Sem par, a referência só se apaga (o calendário próprio, o id da peça desenhada). */
  readonly optional?: true;
  /**
   * Sem par, a referência fica como está — o histórico (a alocação desfeita, a marca desfeita, a
   * mudança de planejamento) aponta para o que existia quando aconteceu, e não para a aprovação.
   */
  readonly keepIfMissing?: true;
}

/** Uma parte da chave única: um valor da linha, ou a referência de índice `ref` já traduzida. */
export type UniqueKeyPart = { readonly ref: number } | { readonly value: string };

export interface OperationRow {
  /** A tabela, como o repositório a conhece (`weekly_allocations`). */
  readonly table: string;
  readonly id: string;
  /** Como a pessoa a reconhece, na lista de conflitos. */
  readonly label: string;
  readonly refs: readonly OperationRowRef[];
  /** A linha de operação de que esta depende: vai e fica junto com ela. */
  readonly parent?: { readonly table: string; readonly id: string };
  /** As chaves únicas na obra (sem a obra): duas iguais na obra nova são o mesmo lugar. */
  readonly uniqueKeys?: readonly (readonly UniqueKeyPart[])[];
  /** Não pode ficar na antiga: a alocação é do recurso, que vai (`0008`). */
  readonly mustMove?: true;
  /** A referência sem par tira a linha, em vez de parar a aprovação (a disciplina marcada). */
  readonly dropIfMissing?: true;
  /** A da versão que perde o mesmo lugar sai (exclusão lógica). Sem isto, ela tem de ficar. */
  readonly canDiscard?: true;
  /**
   * Histórico (a linha excluída): a referência sem par não pede escolha. Sem `keepIfMissing` na
   * referência, a linha fica na antiga (o item do cronograma, cuja chave composta exige a frente
   * na mesma obra).
   */
  readonly history?: true;
}

export interface TransferSide {
  readonly targets: readonly TransferTarget[];
  readonly rows: readonly OperationRow[];
}

export interface TransferSnapshot {
  /** A aprovada antiga: a operação que vai. */
  readonly from: TransferSide;
  /** A versão que vira aprovada: os alvos novos, e a operação que ela já tinha. */
  readonly to: TransferSide;
  /** Cópia → origem, das duas obras e de quem veio antes delas (`project_copy_origins`). */
  readonly origins: ReadonlyMap<string, string>;
  /** A ordem das tabelas: pai antes do filho. As de fora vêm no fim, na ordem em que chegaram. */
  readonly tableOrder: readonly string[];
}

/** `KEEP`: as linhas que apontam para o alvo ficam na antiga. */
export const KEEP_IN_APPROVED = 'KEEP';

export interface TransferChoices {
  /** Sem lugar: por alvo (`chave do alvo`), o id do alvo na versão ou `KEEP`. */
  readonly missing?: Readonly<Record<string, string>>;
  /** No mesmo lugar: por conflito (`key`), o id da linha que vale. */
  readonly collisions?: Readonly<Record<string, string>>;
}

export function targetKey(kind: TransferTargetKind, id: string): string {
  return `${kind}:${id}`;
}

export interface MissingTarget {
  readonly key: string;
  readonly kind: TransferTargetKind;
  readonly label: string;
  /** Quantas linhas de cada tabela apontam para ele (com as que dependem delas). */
  readonly rows: readonly { readonly table: string; readonly count: number }[];
  /** Falso quando alguma delas não pode ficar na antiga: só um alvo novo resolve. */
  readonly canKeep: boolean;
  /** Os alvos do mesmo tipo na versão. */
  readonly options: readonly { readonly id: string; readonly label: string }[];
}

export interface TransferCollision {
  readonly key: string;
  readonly table: string;
  readonly candidates: readonly {
    readonly id: string;
    readonly label: string;
    readonly from: 'APPROVED' | 'VERSION';
    /** Perdendo, para onde vai: fica na antiga, sai, ou não pode perder. */
    readonly ifLoses: 'KEEP' | 'DISCARD' | 'CANNOT_LOSE';
  }[];
}

export type RowFate = 'MOVE' | 'KEEP' | 'DISCARD';

export interface TransferPlan {
  /** Alvo da aprovada → alvo da versão (ou `null`: a referência opcional se apaga). */
  readonly mapping: ReadonlyMap<string, string | null>;
  /** Por tabela, as linhas da aprovada que vão. */
  readonly move: ReadonlyMap<string, readonly string[]>;
  /** Por tabela, as linhas da aprovada que ficam. */
  readonly keep: ReadonlyMap<string, readonly string[]>;
  /** Por tabela, as linhas que saem: a da versão que perdeu, a marca sem disciplina. */
  readonly discard: ReadonlyMap<string, readonly string[]>;
  readonly missing: readonly MissingTarget[];
  readonly collisions: readonly TransferCollision[];
}

/** A raiz de uma linha copiada: a origem da origem, até não haver mais. */
function raizDe(id: string, origins: ReadonlyMap<string, string>): string {
  let atual = id;
  const vistos = new Set<string>();
  for (;;) {
    const origem = origins.get(atual);
    if (origem === undefined || vistos.has(origem)) return atual;
    vistos.add(atual);
    atual = origem;
  }
}

/** O par de cada alvo da aprovada na versão — pela raiz, e sem ela pelo nome (RN-464, RN-466). */
function paresDosAlvos(snapshot: TransferSnapshot): Map<string, string> {
  const pares = new Map<string, string>();
  const porTipo = <T extends { readonly kind: TransferTargetKind }>(lista: readonly T[]) => {
    const mapa = new Map<TransferTargetKind, T[]>();
    for (const item of lista) mapa.set(item.kind, [...(mapa.get(item.kind) ?? []), item]);
    return mapa;
  };
  const daVersao = porTipo(snapshot.to.targets.filter((alvo) => alvo.kind !== 'PIECE'));
  const daAprovada = porTipo(snapshot.from.targets.filter((alvo) => alvo.kind !== 'PIECE'));

  for (const [kind, antigos] of daAprovada) {
    const novos = daVersao.get(kind) ?? [];
    const porRaiz = new Map(novos.map((novo) => [raizDe(novo.id, snapshot.origins), novo]));
    const tomados = new Set<string>();
    const semPar: TransferTarget[] = [];
    for (const antigo of antigos) {
      const par = porRaiz.get(raizDe(antigo.id, snapshot.origins));
      if (par === undefined) {
        semPar.push(antigo);
        continue;
      }
      pares.set(targetKey(kind, antigo.id), par.id);
      tomados.add(par.id);
    }
    // Pelo nome, só entre as da versão que ninguém tomou pela origem.
    const livresPorNome = new Map<string, TransferTarget>();
    for (const novo of novos) {
      if (tomados.has(novo.id) || novo.naturalKey === '') continue;
      if (!livresPorNome.has(novo.naturalKey)) livresPorNome.set(novo.naturalKey, novo);
    }
    for (const antigo of semPar) {
      if (antigo.naturalKey === '') continue;
      const par = livresPorNome.get(antigo.naturalKey);
      if (par === undefined) continue;
      pares.set(targetKey(kind, antigo.id), par.id);
      livresPorNome.delete(antigo.naturalKey);
    }
  }
  return pares;
}

/** A peça da planilha: o par é o mesmo nome na tipologia par, se a versão o tem. */
function parDaPeca(
  antigo: TransferTarget,
  tipologias: ReadonlyMap<string, string | null>,
  pecasDaVersao: ReadonlySet<string>,
  pelaPlanilha: ReadonlyMap<string, string>,
): string | undefined {
  const corte = antigo.id.indexOf('|');
  const tipologia = tipologias.get(targetKey('UNIT_TYPE', antigo.id.slice(0, corte)));
  if (tipologia === undefined || tipologia === null) return undefined;
  const novo = `${tipologia}|${antigo.id.slice(corte + 1)}`;
  if (pecasDaVersao.has(novo)) return novo;
  // Renomeada numa das obras: o mesmo nome de planilha na tipologia par (RN-458).
  return antigo.naturalKey === ''
    ? undefined
    : pelaPlanilha.get(`${tipologia}|${antigo.naturalKey}`);
}

/**
 * O plano da transferência: o par de cada alvo, o destino de cada linha da aprovada e o que
 * ainda espera uma escolha. Com `missing` e `collisions` vazios, a aprovação pode seguir.
 */
export function planOperationTransfer(
  snapshot: TransferSnapshot,
  choices: TransferChoices = {},
): TransferPlan {
  const alvosDaAprovada = new Map(
    snapshot.from.targets.map((alvo) => [targetKey(alvo.kind, alvo.id), alvo]),
  );
  const alvosDaVersao = new Map(
    snapshot.to.targets.map((alvo) => [targetKey(alvo.kind, alvo.id), alvo]),
  );
  const pecasDaVersao = new Set(
    snapshot.to.targets.filter((alvo) => alvo.kind === 'PIECE').map((alvo) => alvo.id),
  );
  const pelaPlanilha = new Map(
    snapshot.to.targets
      .filter((alvo) => alvo.kind === 'PIECE' && alvo.naturalKey !== '')
      .map((alvo) => [`${alvo.id.slice(0, alvo.id.indexOf('|'))}|${alvo.naturalKey}`, alvo.id]),
  );

  // 1. O par de cada alvo, e as escolhas para os que não têm.
  const mapping = new Map<string, string | null>(paresDosAlvos(snapshot));
  for (const alvo of snapshot.from.targets) {
    if (alvo.kind !== 'PIECE') continue;
    const par = parDaPeca(alvo, mapping, pecasDaVersao, pelaPlanilha);
    if (par !== undefined) mapping.set(targetKey('PIECE', alvo.id), par);
  }
  const escolhaDe = (chave: string): string | undefined => {
    const escolha = choices.missing?.[chave];
    if (escolha === undefined) return undefined;
    if (escolha === KEEP_IN_APPROVED) return escolha;
    const kind = chave.slice(0, chave.indexOf(':')) as TransferTargetKind;
    // O alvo escolhido tem de ser da versão, e do mesmo tipo.
    return alvosDaVersao.has(targetKey(kind, escolha)) ? escolha : undefined;
  };

  // 2. O destino de cada linha da aprovada, na ordem das tabelas (pai antes do filho).
  const ordem = new Map(snapshot.tableOrder.map((tabela, indice) => [tabela, indice]));
  const linhas = [...snapshot.from.rows].sort(
    (a, b) => (ordem.get(a.table) ?? ordem.size) - (ordem.get(b.table) ?? ordem.size),
  );
  const destino = new Map<string, RowFate>();
  const linhaChave = (tabela: string, id: string) => `${tabela}#${id}`;
  /** Por alvo sem par: as linhas que apontam para ele, sem escolha ainda. */
  const pendentes = new Map<string, OperationRow[]>();

  for (const linha of linhas) {
    const doPai =
      linha.parent === undefined
        ? undefined
        : destino.get(linhaChave(linha.parent.table, linha.parent.id));
    if (doPai === 'KEEP' || doPai === 'DISCARD') {
      destino.set(linhaChave(linha.table, linha.id), doPai);
      continue;
    }
    let fate: RowFate = 'MOVE';
    for (const ref of linha.refs) {
      const chave = targetKey(ref.kind, ref.id);
      if (mapping.has(chave)) continue;
      if (ref.optional === true) {
        mapping.set(chave, null);
        continue;
      }
      if (ref.keepIfMissing === true) continue;
      if (linha.history === true && linha.mustMove !== true) {
        fate = 'KEEP';
        continue;
      }
      if (linha.dropIfMissing === true) {
        fate = 'DISCARD';
        break;
      }
      const escolha = escolhaDe(chave);
      if (escolha === KEEP_IN_APPROVED) {
        fate = 'KEEP';
        continue;
      }
      if (escolha !== undefined) {
        mapping.set(chave, escolha);
        continue;
      }
      pendentes.set(chave, [...(pendentes.get(chave) ?? []), linha]);
    }
    destino.set(linhaChave(linha.table, linha.id), fate);
  }

  // A alocação não pode ficar: o alvo dela só se resolve com um alvo novo.
  const descendentes = new Map<string, OperationRow[]>();
  for (const linha of linhas) {
    if (linha.parent === undefined) continue;
    const pai = linhaChave(linha.parent.table, linha.parent.id);
    descendentes.set(pai, [...(descendentes.get(pai) ?? []), linha]);
  }
  const comDescendentes = (raiz: OperationRow): OperationRow[] => {
    const todas: OperationRow[] = [];
    const fila = [raiz];
    while (fila.length > 0) {
      const linha = fila.shift()!;
      todas.push(linha);
      fila.push(...(descendentes.get(linhaChave(linha.table, linha.id)) ?? []));
    }
    return todas;
  };
  // Quem fica por causa de uma escolha leva os descendentes que ainda iam.
  for (const linha of linhas) {
    if (destino.get(linhaChave(linha.table, linha.id)) !== 'KEEP') continue;
    for (const filha of comDescendentes(linha))
      destino.set(linhaChave(filha.table, filha.id), 'KEEP');
  }

  const missing: MissingTarget[] = [...pendentes.entries()].map(([chave, apontam]) => {
    const alvo = alvosDaAprovada.get(chave);
    const kind = chave.slice(0, chave.indexOf(':')) as TransferTargetKind;
    const todas = apontam.flatMap(comDescendentes);
    const porTabela = new Map<string, number>();
    for (const linha of new Map(todas.map((l) => [linhaChave(l.table, l.id), l])).values()) {
      porTabela.set(linha.table, (porTabela.get(linha.table) ?? 0) + 1);
    }
    return {
      key: chave,
      kind,
      label: alvo?.label ?? chave,
      rows: [...porTabela.entries()].map(([table, count]) => ({ table, count })),
      canKeep: !todas.some((linha) => linha.mustMove === true),
      options: snapshot.to.targets
        .filter((candidato) => candidato.kind === kind)
        .map((candidato) => ({ id: candidato.id, label: candidato.label })),
    };
  });

  // 3. O mesmo lugar: as chaves únicas das que vão, contra as que a versão já tem e entre si.
  const chaveDaLinha = (linha: OperationRow, traduzir: boolean): string[] =>
    (linha.uniqueKeys ?? []).map(
      (partes, indice) =>
        `${linha.table}#${String(indice)}#` +
        partes
          .map((parte) => {
            if ('value' in parte) return parte.value;
            const ref = linha.refs[parte.ref];
            if (ref === undefined) return '';
            if (!traduzir) return ref.id;
            return String(mapping.get(targetKey(ref.kind, ref.id)) ?? `?${ref.id}`);
          })
          .join('|'),
    );
  type Ocupante = { readonly linha: OperationRow; readonly from: 'APPROVED' | 'VERSION' };
  const ocupantes = new Map<string, Ocupante[]>();
  for (const linha of linhas) {
    if (destino.get(linhaChave(linha.table, linha.id)) !== 'MOVE') continue;
    for (const chave of chaveDaLinha(linha, true)) {
      ocupantes.set(chave, [...(ocupantes.get(chave) ?? []), { linha, from: 'APPROVED' }]);
    }
  }
  for (const linha of snapshot.to.rows) {
    for (const chave of chaveDaLinha(linha, false)) {
      if (!ocupantes.has(chave)) continue;
      ocupantes.set(chave, [...ocupantes.get(chave)!, { linha, from: 'VERSION' }]);
    }
  }

  const descartadas = new Map<string, string[]>();
  const descartar = (linha: OperationRow) =>
    descartadas.set(linha.table, [...(descartadas.get(linha.table) ?? []), linha.id]);
  const collisions: TransferCollision[] = [];
  for (const [chave, quem] of ocupantes) {
    if (quem.length < 2) continue;
    const vale = choices.collisions?.[chave];
    const vencedor = quem.find((ocupante) => ocupante.linha.id === vale);
    // A da aprovada que perde fica na antiga com tudo o que depende dela: só pode se nenhuma
    // dessas linhas tiver de ir (a alocação, que é do recurso).
    const perdedorPode = (ocupante: Ocupante) =>
      ocupante.from === 'APPROVED'
        ? !comDescendentes(ocupante.linha).some((linha) => linha.mustMove === true)
        : ocupante.linha.canDiscard === true;
    const perdedores = quem.filter((ocupante) => ocupante !== vencedor);
    if (vencedor !== undefined && perdedores.every(perdedorPode)) {
      for (const perdedor of perdedores) {
        if (perdedor.from === 'VERSION') {
          descartar(perdedor.linha);
          continue;
        }
        for (const filha of comDescendentes(perdedor.linha)) {
          destino.set(linhaChave(filha.table, filha.id), 'KEEP');
        }
      }
      continue;
    }
    collisions.push({
      key: chave,
      table: quem[0]!.linha.table,
      candidates: quem.map((ocupante) => ({
        id: ocupante.linha.id,
        label: ocupante.linha.label,
        from: ocupante.from,
        ifLoses: !perdedorPode(ocupante)
          ? 'CANNOT_LOSE'
          : ocupante.from === 'APPROVED'
            ? 'KEEP'
            : 'DISCARD',
      })),
    });
  }

  // 4. As listas por tabela.
  const move = new Map<string, string[]>();
  const keep = new Map<string, string[]>();
  for (const linha of linhas) {
    const fate = destino.get(linhaChave(linha.table, linha.id)) ?? 'MOVE';
    const alvo = fate === 'MOVE' ? move : fate === 'KEEP' ? keep : descartadas;
    alvo.set(linha.table, [...(alvo.get(linha.table) ?? []), linha.id]);
  }

  return { mapping, move, keep, discard: descartadas, missing, collisions };
}

/** A transferência está pronta: nada espera escolha. */
export function isTransferReady(plan: TransferPlan): boolean {
  return plan.missing.length === 0 && plan.collisions.length === 0;
}
